import { Application, DownloadDatasetQuery, GetDataResult, Ltree, OutPut } from "@/types/generated/UsedGeneratedTypes";
import { Fetcher, UploadCompleteEvent, UploadProgressEvent } from "../Fetcher";
import { EtagCache } from "../EtagCache";
import { AddDataResult } from "@/types/front/AddDataResult";
import { FileService } from "./FileService";
import { ref } from "vue";

/**
 * Etat publie par le dernier appel a {@link DataService.getDataFilters} :
 * valeur du header HTTP {@code X-Filter-Cache} ( HIT / MISS / PARTIAL /
 * DISABLED ) , ou {@code null} si le payload a ete reservi depuis le cache
 * ETag cote client ( aucun nouveau header n'a ete observe ) .
 *
 * <p>Exposition deliberement via un ref module-level pour eviter de
 * propager le statut dans toutes les signatures intermediaires ( fetcher
 * -> service -> composable -> composant ) et garder les types existants
 * intacts ( non-breaking ) . Le bloc filtre lit cette valeur reactivement .
 */
export const lastFilterCacheStatus = ref<string | null>(null);

/**
 * Wire payload du endpoint `GET /applications/{app}/data/{dataType}/filters` .
 *
 * <p>{@code entries} : liste des {@code FilterListEntry} ( union des
 * {@code FilterList} legacy + {@code ColumnDistinctValues} pour les colonnes
 * {@code __FILTER_LIST__} / {@code __FILTER_TEXT__} ) . Consommé par
 * {@code buildFilterListMap} pour produire la map filterList du frontend .
 *
 * <p>{@code variables} : ensemble des componentKeys filtrables ( = colonnes
 * que le bloc filtre doit afficher ) . Permet au frontend de rendre le
 * bloc filtre dès l'arrivée de {@code /filters} sans attendre {@code /data} .
 * Iso-source backend ( {@code FilterListVariablesExtractor} ) : aucune
 * column de cette liste ne produira 'aucun filtre disponible' côté UI .
 *
 * @since 2026-05-23 ( cache filter columns - sprint cache )
 */
export interface FilterListResponse {
  entries: unknown[];
  variables: string[];
}

// ─── Interfaces de dépendances injectables ─────────────────────────────────────

export interface DataServiceI18n {
  getLocale: () => string | null;
}

export interface DataServiceDependencies {
  internationalisationService?: DataServiceI18n | null;
}

/**
 * Vue stable et plate du statut d'un workflow exposee aux consumers
 * ( polling UI dans DataVersioningView ) . Decouple le frontend du wire
 * format backend ( {@code DashboardWorkflowDTO.Detail} avec champs
 * imbriques {@code summary.status} ) pour eviter de re-casser le polling
 * si le DTO backend evolue .
 */
export interface WorkflowStatusView {
  correlationId: string;
  workflowType: string;
  status: string;
  startTime?: string;
  endTime?: string | null;
  durationMs?: number | null;
  recordsProcessed?: number;
  recordsTotal?: number;
  chunksProcessed?: number;
  progressPercentage?: number | null;
  fatalError?: string | null;
}

/**
 * Wire format renvoye par {@code GET /api/dashboard/workflows/{cid}} .
 * Usage interne uniquement : la methode {@code getWorkflowStatus} aplatit
 * vers {@link WorkflowStatusView} avant de retourner aux consumers .
 */
interface DashboardWorkflowDetail {
  summary?: {
    correlationId?: string;
    workflowType?: string;
    status?: string;
    startTime?: string;
    endTime?: string | null;
    durationMs?: number | null;
    recordsProcessed?: number;
    recordsTotal?: number;
    chunksProcessed?: number;
    progressPercentage?: number | null;
    fatalError?: string | null;
  };
  fatalError?: string | null;
  errors?: unknown;
  metadata?: Record<string, unknown>;
}

export class DataService extends Fetcher {
  static INSTANCE: DataService = new DataService();
  static #dependencies: DataServiceDependencies = {};

  constructor() {
    super();
  }

  static configureService(deps: DataServiceDependencies): void {
    DataService.#dependencies = { ...DataService.#dependencies, ...deps };
  }

  static resetDependencies(): void {
    DataService.#dependencies = {};
  }

  protected get _i18nService(): DataServiceI18n | null {
    return DataService.#dependencies.internationalisationService ?? null;
  }

  /**
   * Cache ETag JS-side pour /data/json. Borne plus serrée que filterList :
   * le payload moyen est ~25× plus gros ( 5 MB vs 200 KB ) , donc on
   * dimensionne 5 entrées / 10 MB max pour ne pas saturer la mémoire de
   * l'onglet sur sessions longues. Ces caps peuvent être surchargés par
   * /session/config si besoin ( resize à chaud ).
   */
  static readonly #dataJsonEtagCache = new EtagCache<GetDataResult>({ maxEntries: 5, maxBytes: 10 * 1024 * 1024 });

  async getData(applicationName: string, dataName: string, params?: DownloadDatasetQuery, loadExample?: boolean): Promise<GetDataResult> {
    const url = `applications/${applicationName}/data/${dataName}/json`;
    const queryParams: Record<string, unknown> = {};
    let cacheKey: string;
    if (params) {
      const locale = this._i18nService?.getLocale() ?? "";
      params.outPut = params.outPut || {} as OutPut;
      params.outPut.locale = locale;
      queryParams.downloadDatasetQuery = JSON.stringify(params);
      queryParams.loadExample = !!loadExample;
      cacheKey = `${applicationName}::${dataName}::${queryParams.downloadDatasetQuery}::${queryParams.loadExample}`;
    }
    else {
      cacheKey = `${applicationName}::${dataName}`;
    }
    return this.getJsonWithEtag<GetDataResult>(
      url,
      queryParams,
      DataService.#dataJsonEtagCache,
      cacheKey,
    );
  }

  static clearDataJsonEtagCache(): void {
    DataService.#dataJsonEtagCache.clear();
  }

  static configureDataJsonEtagCache(options: { maxEntries?: number; maxBytes?: number }): void {
    DataService.#dataJsonEtagCache.resize(options);
  }

  static getDataJsonEtagCacheStats(): { entries: number; bytes: number; capacityEntries: number; capacityBytes: number } {
    return {
      entries: DataService.#dataJsonEtagCache.size(),
      bytes: DataService.#dataJsonEtagCache.totalBytes(),
      capacityEntries: DataService.#dataJsonEtagCache.capacity(),
      capacityBytes: DataService.#dataJsonEtagCache.byteCapacity(),
    };
  }

  async getDataByNaturalKey(applicationName: string, dataName: string, referenceKey: unknown): Promise<GetDataResult> {
    const params: DownloadDatasetQuery = {
      naturalKeys: [referenceKey as Ltree], // l'API accepte tout identifiant naturel sérialisable
      application: {} as unknown as Application,
      dataName: "",
      outPut: {} as unknown as OutPut,
      offset: 0,
      limit: 10,
      rowIds: [],
      componentSelects: [],
      componentFilters: [],
      componentOrderBy: [],
      authorizationDescriptions: [],
      horizontalDisplay: false,
    };
    return await this.getData(applicationName, dataName, params);
  }

  /**
   * PERF #465 — Charge les listes de filtres (dropdowns de références) via un endpoint dédié.
   * Séparé de getData() pour permettre un chargement asynchrone : les données s'affichent
   * immédiatement (~1-2s) tandis que les filtres se chargent en arrière-plan (~54s au 1er appel,
   * puis quasi-instantané grâce au cache backend de 10 minutes).
   *
   * PERF audit (8/5/26) - revalidation conditionnelle via ETag côté JS ( cf.
   * {@link Fetcher#getJsonWithEtag} ). Le cache LRU est process-local , vidé
   * au reload ; l'invalidation après import/delete côté backend est portée
   * implicitement par le changement d'ETag ( mismatch -> 200 fresh + update ).
   */
  static readonly #filterListEtagCache = new EtagCache<FilterListResponse>();

  /**
   * Récupère le payload {@code /filters} pour un datatype .
   *
   * <p><b>Shape de la réponse</b> : {@code {entries: [...], variables: [...]}} ( cf
   * commit feat(filters): payload /filters retourne {entries, variables} côté
   * backend ) .
   * <ul>
   *   <li>{@code entries} : valeurs distinctes par colonne filtrable
   *       ( {@code FilterListEntry} = {@code FilterList} legacy ou
   *       {@code ColumnDistinctValues} pour les colonnes {@code __FILTER_LIST__} ) ;</li>
   *   <li>{@code variables} : componentKeys filtrables ( ReferenceChecker
   *       columns + FILTER_LIST/TEXT + interval Float/Integer/Date ) . Permet
   *       au frontend de rendre le bloc filtre dès l'arrivée de {@code /filters}
   *       SANS attendre {@code /data} ( gain UX gros datasets ) .</li>
   * </ul>
   *
   * <p><b>Iso-source</b> : les {@code variables} sont dérivées des {@code entries}
   * côté backend ( {@code FilterListVariablesExtractor} ) , garantissant que
   * toute column dans {@code variables} sera matchable par {@code ReferenceFilter}
   * côté frontend ( pas de bug 'aucun filtre disponible' sur colonnes ref ) .
   *
   * <p>Cache : {@code openadom.cache.filter-list.enabled} ( default 10min , flag
   * backend ) . ETag JS-side cache process-local via {@link Fetcher#getJsonWithEtag} .
   */
  async getDataFilters(applicationName: string, dataName: string): Promise<FilterListResponse> {
    return this.getJsonWithEtag<FilterListResponse>(
      `applications/${applicationName}/data/${dataName}/filters`,
      {},
      DataService.#filterListEtagCache,
      `${applicationName}::${dataName}`,
      (headers, fromClientCache) => {
        // Side-channel diagnostic : expose la valeur du header X-Filter-Cache
        // ( HIT / MISS / PARTIAL / DISABLED ) au bloc filtre via le ref
        // module-level . Quand le payload est servi depuis le cache ETag
        // cote client ( fromClientCache=true ) on garde l'etat precedent
        // pour ne pas afficher de transition trompeuse .
        if (fromClientCache) return;
        lastFilterCacheStatus.value = headers.get("X-Filter-Cache");
      },
    );
  }

  /**
   * Réservé aux tests + invalidation manuelle ( logout , changement
   * d'utilisateur ). À ne pas utiliser dans le code applicatif normal :
   * l'invalidation correcte passe par le mismatch d'ETag côté backend.
   */
  static clearFilterListEtagCache(): void {
    DataService.#filterListEtagCache.clear();
  }

  /**
   * Reconfigure les caps du cache filterList à partir d'options runtime
   * ( typiquement lues depuis /session/config au boot de l'app , exposées
   * par le backend via openadom.cache.front.etag.* ). Permet un tuning
   * sans rebuilder le bundle frontend.
   */
  static configureFilterListEtagCache(options: { maxEntries?: number; maxBytes?: number }): void {
    DataService.#filterListEtagCache.resize(options);
  }

  /**
   * Statistiques d'observabilité pour debug / panneau admin.
   */
  static getFilterListEtagCacheStats(): { entries: number; bytes: number; capacityEntries: number; capacityBytes: number } {
    return {
      entries: DataService.#filterListEtagCache.size(),
      bytes: DataService.#filterListEtagCache.totalBytes(),
      capacityEntries: DataService.#filterListEtagCache.capacity(),
      capacityBytes: DataService.#filterListEtagCache.byteCapacity(),
    };
  }

  async getDataZip(applicationName: string, dataTypeId: string, params: DownloadDatasetQuery): Promise<URL> {
    return this.getPath(
      `applications/${applicationName}/data/${dataTypeId}/zip`,
      {
        downloadDatasetQuery: JSON.stringify(params),
      },
    );
  }

  async addData(
    applicationName: string,
    dataName: string,
    dataFile: File | null,
    params: Record<string, unknown>,
    onUploadProgress?: (e: UploadProgressEvent) => void,
    onUploadComplete?: (e: UploadCompleteEvent) => void,
  ): Promise<AddDataResult> {
    return this.post(
      `applications/${applicationName}/data/${dataName}`,
      {
        file: dataFile,
        params: JSON.stringify(params),
      },
      null,
      undefined,
      onUploadProgress,
      onUploadComplete,
    );
  }

  /**
   * Toggle the published flag of a binary file via the 2-phase async
   * endpoint ( see backend PUBLISH_UNPUBLISH.md ). Phase 1 is synchronous
   * and returns HTTP 202 + correlationId : the visible flag is already
   * toggled , workflow_log row exists in IN_PROGRESS , START mail sent .
   * Phase 2 ( cascade INSERT for publish , SQL DELETE for unpublish )
   * runs asynchronously ; the END mail confirms completion .
   *
   * <p>The caller may use {@code correlationId} to poll workflow_log
   * progress ; for V1 the UI just trusts the optimistic flag toggle and
   * relies on the mail to confirm the heavy work is done.
   */
  async togglePublishedFlag(
    applicationName: string,
    dataName: string,
    fileId: string,
    published: boolean,
  ): Promise<{ fileId: string; published: boolean; dataName: string; correlationId: string; status: string }> {
    const url = `applications/${applicationName}/data/${dataName}/files/${fileId}/publish?published=${published}`;
    const result = await this.post<{
      fileId: string;
      published: boolean;
      dataName: string;
      correlationId: string;
      status: string;
    }>(url, {});
    // Mutation server-side : invalidate the /filesOnRepository ETag cache
    // so the next listing reads fresh published flags.
    // Fix P1-FRONT-1 : invalider AUSSI dataJsonEtagCache + filterListEtagCache
    // pour que DataTableView et FilterBar voient les donnees fraiches sans F5 .
    FileService.clearFilesOnRepositoryEtagCache();
    try { DataService.clearDataJsonEtagCache(); } catch { /* methode optionnelle si non exposee */ }
    try { DataService.clearFilterListEtagCache(); } catch { /* idem */ }
    return result;
  }

  async deleteReferenceValuesByKey(applicationName: string, dataName: string, rowId: string): Promise<string> {
    const params = { rowIds: [rowId] };
    return this.delete(
      `applications/${applicationName}/data/${dataName}`,
      params,
    );
  }

  /**
   * Récupère le statut courant d'un workflow ( publish / unpublish / delete ) .
   * Utilisé pour le polling côté UI : afficher un spinner tant que le workflow
   * est IN_PROGRESS , basculer en état terminal sur COMPLETED / FAILED / CANCELLED .
   *
   * <p>Endpoint : {@code GET /api/dashboard/workflows/{correlationId}} retourne
   * un {@code DashboardWorkflowDTO.Detail} avec structure imbriquée
   * {@code { summary: { status, ... }, fatalError, errors, metadata }} . Cette
   * methode aplatit la reponse en une vue stable {@link WorkflowStatusView} pour
   * que les consumers ( polling UI ) ne dependent pas du wire format backend .
   *
   * <p>Retourne 404 si le workflow n'existe pas / plus visible ( le caller doit
   * traiter le rejet comme un etat terminal pour cleanup ) .
   */
  async getWorkflowStatus(correlationId: string): Promise<WorkflowStatusView> {
    const raw = await this.get<DashboardWorkflowDetail>(
      `dashboard/workflows/${correlationId}`,
    );
    return {
      correlationId: raw.summary?.correlationId ?? correlationId,
      workflowType: raw.summary?.workflowType ?? "",
      status: raw.summary?.status ?? "UNKNOWN",
      startTime: raw.summary?.startTime,
      endTime: raw.summary?.endTime,
      durationMs: raw.summary?.durationMs,
      recordsProcessed: raw.summary?.recordsProcessed,
      recordsTotal: raw.summary?.recordsTotal,
      chunksProcessed: raw.summary?.chunksProcessed,
      progressPercentage: raw.summary?.progressPercentage,
      fatalError: raw.fatalError ?? null,
    };
  }

  /**
   * Annule un workflow en cours ( publish / unpublish / delete ) . Le serveur
   * marque le workflow CANCELLED ( + WorkflowEventBus.cancel ) ; le handler
   * Phase 2 detectera l'annulation au prochain checkpoint et sortira proprement
   * sans toucher aux donnees .
   *
   * <p>Endpoint : {@code DELETE /api/dashboard/workflows/{correlationId}} .
   */
  async cancelWorkflow(correlationId: string): Promise<{ cancelled: boolean; reason?: string }> {
    return this.delete(`dashboard/workflows/${correlationId}`, {});
  }
}
